/* global $3Dmol */ 
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Space, Button, Typography, message, Checkbox, Divider, Collapse,
    Spin, Alert, Layout, Row, Col, Tooltip, Slider,
    Radio,
} from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { postRequest } from "../../util/service";
import { ArrowLeftOutlined, RedoOutlined } from "@ant-design/icons";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Sider, Content } = Layout;

const cloneCameraView = (view) => {
    if (!view) return null;
    if (Array.isArray(view)) return [...view];
    if (typeof view.slice === "function") return view.slice();
    return view;
};

// --- Helper Functions for Pharmacophore Rendering ---
const featureStyles = {
    "Aromatic": { color: 0xffa500, css: "#FFA500", label: "(R) Aromatic Ring" },
    "Acceptor": { color: 0xff0000, css: "#E87A7A", label: "(A) Acceptor" },
    "Donor": { color: 0x0000ff, css: "#7AB8E8", label: "(D) Donor" },
    "Hydrophobic": { color: 0x00ff00, css: "#7AE88E", label: "(H) Hydrophobic" },
    "NegativeIonic": { color: 0xE83E3E, css: "#808080", label: "(N) Negative Ionic" },
    "PositiveIonic": { color: 0x3E7EE8, css: "#808080", label: "(P) Positive Ionic" },
    "Default": { color: 0x808080, css: "#808080", label: "Default" }
};

function parseHypothesisPdb(pdbString) {
    if (!pdbString) return [];
    const features = [];
    const lines = pdbString.split("\n");
    for (const line of lines) {
        if (line.startsWith("ATOM") || line.startsWith("HETATM")) {
            const residueName = line.substring(17, 20).trim();
            const label = line.substring(12, 16).trim() + line.substring(22, 26).trim();
            const x = parseFloat(line.substring(30, 38));
            const y = parseFloat(line.substring(38, 46));
            const z = parseFloat(line.substring(46, 54));
            const radius = parseFloat(line.substring(60, 66));
            let type = "Default";
            if (residueName === "ARO") type = "Aromatic";
            else if (residueName === "ACC") type = "Acceptor";
            else if (residueName === "DON") type = "Donor";
            else if (residueName === "HYD") type = "Hydrophobic";
            features.push({ type, label, position: [x, y, z], radius: isNaN(radius) ? 1.5 : radius, direction: (type === "Acceptor" || type === "Donor") ? [0, 1, 0] : null });
        }
    }
    return features;
}


// ✅ NEW: A component to render the legend
const PharmacophoreLegend = () => {
    const legendItems = [
        featureStyles["Acceptor"],
        featureStyles["Donor"],
        featureStyles["Hydrophobic"],
        featureStyles["NegativeIonic"],
        featureStyles["PositiveIonic"],
        featureStyles["Aromatic"],
    ];

    return (
        <Space size="middle" wrap>
            {legendItems.map(item => (
                <Space key={item.label} size={4}>
                    <div style={{ width: 15, height: 15, backgroundColor: item.css, border: "1px solid #ccc" }} />
                    <Text style={{ fontSize: "12px" }}>{item.label}</Text>
                </Space>
            ))}
        </Space>
    );
};

export default function Molecule3DPage() {
    const navigate = useNavigate();
    const { projectId, batchId } = useParams();
    const { state } = useLocation();
    const { proteinPdbFile, ligandPdbFile, phaseId } = state || {};

    const [hypothesisPdb, setHypothesisPdb] = useState("");
    const [ligandPdb, setLigandPdb] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

 // ✅ ADDED: More detailed state for Ligand controls
    const [showLigand, setShowLigand] = useState(true);
    const [ligandBaseStyles, setLigandBaseStyles] = useState(["stick"]);
    const [ligandColorScheme, setLigandColorScheme] = useState("Jmol");
    const [showLigandSurface, setShowLigandSurface] = useState(false);

    // State for Pharmacophore controls
    const [showPharmacophore, setShowPharmacophore] = useState(true);
    const [showFeatureLabels, setShowFeatureLabels] = useState(true);
    const [glassSphereOpacity, setGlassSphereOpacity] = useState(0.5);

    const viewerDiv = useRef(null);
    const viewer = useRef(null);
    const ligandModelRef = useRef(null);
    const initialViewRef = useRef(null);

    const fetchPdbFromUrl = useCallback(async (pdbFile) => {
        if (!pdbFile) return;
        try {
            const payload = { projectId, batchId, usecase: "phase", usecaseId: phaseId, pdbFile };
            const urlResponse = await postRequest("/riva/sd/pdb", payload);
            if (!urlResponse?.data?.url) throw new Error(`Could not get URL for ${pdbFile}`);
            const pdbResponse = await fetch(urlResponse.data.url);
            if (!pdbResponse.ok) throw new Error(`Failed to download PDB from S3 for ${pdbFile}`);
            return await pdbResponse.text();
        } catch (err) {
            console.error(err);
            throw err;
        }
    }, [projectId, batchId, phaseId]);

    useEffect(() => {
        if (!ligandPdbFile || !proteinPdbFile) {
            setError("File information is missing.");
            setLoading(false);
            return;
        }
        const loadAllData = async () => {
            setLoading(true);
            setError("");
            try {
                const [hypothesisResult, ligandResult] = await Promise.allSettled([
                    fetchPdbFromUrl(proteinPdbFile),
                    fetchPdbFromUrl(ligandPdbFile),
                ]);
                if (hypothesisResult.status === "fulfilled") { setHypothesisPdb(hypothesisResult.value); } 
                else { throw hypothesisResult.reason; }
                if (ligandResult.status === "fulfilled") { setLigandPdb(ligandResult.value); } 
                else { throw ligandResult.reason; }
            } catch (err) {
                const errorMessage = err.message || "An error occurred while loading PDB data.";
                setError(errorMessage);
                message.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };
        loadAllData();
    }, [proteinPdbFile, ligandPdbFile, fetchPdbFromUrl]);

     useEffect(() => {
        if (viewerDiv.current && window.$3Dmol && !viewer.current) {
            viewer.current = window.$3Dmol.createViewer(viewerDiv.current, { backgroundColor: "white", antialias: true });
        }
    }, []);

    useEffect(() => {
        initialViewRef.current = null;
    }, [ligandPdb, hypothesisPdb]);

    const handleResetView = useCallback(() => {
        const v = viewer.current;
        if (!v) return;

        if (initialViewRef.current && typeof v.setView === "function") {
            v.setView(cloneCameraView(initialViewRef.current));
        } else if (typeof v.zoomTo === "function") {
            v.zoomTo();
        }
        v.render();
    }, []);
    
    // Main Rendering Effect
   useEffect(() => {
        const v = viewer.current;
        if (!v || loading) return;
        
        v.clear();
        ligandModelRef.current = null;

        try {
            // Render the Ligand with detailed styles
            if (showLigand && ligandPdb) {
                ligandModelRef.current = v.addModel(ligandPdb, "pdb");
                const ligandSelector = { model: ligandModelRef.current };
                const styles = ligandBaseStyles.length > 0 ? ligandBaseStyles : ["line"];
                styles.forEach((styleKey) => {
                    v.addStyle(ligandSelector, { [styleKey]: { colorscheme: ligandColorScheme } });
                });
                if (showLigandSurface) {
                    v.addSurface($3Dmol.SurfaceType.VDW, { opacity: 0.85, colorscheme: ligandColorScheme }, ligandSelector);
                }
            }

            // Render the Pharmacophore (logic is unchanged)
            if (showPharmacophore && hypothesisPdb) {
                const pharmacophoreData = parseHypothesisPdb(hypothesisPdb);
                pharmacophoreData.forEach(feature => {
                    const style = featureStyles[feature.type] || featureStyles["Default"];
                    const center = new $3Dmol.Vector3(...feature.position);
                    v.addSphere({ center, radius: feature.radius, color: 0xcccccc, alpha: glassSphereOpacity });
                    if (feature.type === "Aromatic") {
                        const normal = new $3Dmol.Vector3(0, 1, 0);
                        let tan1 = new $3Dmol.Vector3(1,0,0);
                        const tan2 = normal.clone().cross(tan1).normalize();
                        let prevPoint = null;
                        for (let i = 0; i <= 32; i++) { 
                            const angle = (i/32) * 2 * Math.PI;
                            const point = center.clone().add(tan1.clone().multiplyScalar(feature.radius * 0.8 * Math.cos(angle))).add(tan2.clone().multiplyScalar(feature.radius * 0.8 * Math.sin(angle)));
                            if (prevPoint) { v.addCylinder({start: prevPoint, end: point, radius: 0.1, color: style.color, fromCap: false, toCap: false}); }
                            prevPoint = point;
                        }
                    } else {
                        v.addSphere({ center, radius: 0.3, color: style.color });
                    }
                    if (showFeatureLabels) {
                        v.addLabel(feature.label, { position: center, fontColor: "black", fontSize: 14, backgroundColor: 0xffffff, backgroundOpacity: 0.6 });
                    }
                    if (feature.direction && (feature.type === "Acceptor" || feature.type === "Donor")) {
                        const dir = new $3Dmol.Vector3(...feature.direction).normalize();
                        const end = center.clone().add(dir.clone().multiplyScalar(feature.radius));
                        v.addArrow({ start: center, end: end, radius: 0.1, color: style.color });
                    }
                });
            }
            if (!initialViewRef.current) {
                v.zoomTo();
                v.render();
                if (typeof v.getView === "function") {
                    initialViewRef.current = cloneCameraView(v.getView());
                }
            } else {
                v.render();
            }
        } catch (err) {
            console.error("3Dmol rendering error:", err);
            message.error("Could not render the 3D data.");
        }
    }, [loading, ligandPdb, hypothesisPdb, showLigand, ligandBaseStyles, ligandColorScheme, showLigandSurface, showPharmacophore, showFeatureLabels, glassSphereOpacity]);
    
    return (
        <div style={{ padding: 24, background: "#f0f2f5", minHeight: "100vh" }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 70 }}>
                            <Col>
                                <Space align="center">
                                    <Tooltip title="Back to Results">
                                        <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
                                    </Tooltip>
                                    <Title level={3} style={{ margin: 0 }}> 3D Pharmacophore Molecule Viewer</Title>
                                </Space>
                            </Col>
                            <Col>
                                <PharmacophoreLegend />
                            </Col>
                        </Row>
            <BreadcrumbTrail />
            <Layout style={{ background: "transparent" }}>
                <Sider theme="light" width={300} style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginRight: "16px", height: "calc(100vh - 160px)", overflowY: "auto" }}>
                    <Row justify="space-between" align="middle" style={{marginBottom: 16}}>
                        <Col><Title level={4} style={{ margin: 0 }}>Controls</Title></Col>
                        <Col><Tooltip title="Reset Camera View"><Button icon={<RedoOutlined />} onClick={handleResetView} /></Tooltip></Col>
                    </Row>
                    {/* ✅ REVERTED: Control panel is simplified back to the original version */}
                    <Collapse defaultActiveKey={["ligand", "pharmacophore"]} bordered={false} style={{ background: "transparent" }}>
                         <Panel header={<Text strong>Ligand Controls</Text>} key="ligand" style={{ border: "none" }}>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Checkbox checked={showLigand} onChange={(e) => setShowLigand(e.target.checked)}>Show Ligand</Checkbox>
                                <Divider style={{margin: "8px 0"}}/>
                                <div style={{opacity: showLigand ? 1 : 0.5}}>
                                    <Text strong>Representation</Text>
                                    <Checkbox.Group 
                                        options={[{ label: "Stick", value: "stick" }, { label: "Line", value: "line" }, { label: "Sphere", value: "sphere" }]} 
                                        value={ligandBaseStyles} 
                                        onChange={setLigandBaseStyles} 
                                        disabled={!showLigand} 
                                    />
                                    <Divider style={{ margin: "12px 0" }} />
                                    <Text strong>Color Scheme</Text>
                                    <Radio.Group 
                                        onChange={(e) => setLigandColorScheme(e.target.value)} 
                                        value={ligandColorScheme} 
                                        disabled={!showLigand}
                                    >
                                        <Space direction="vertical">
                                            <Radio value="Jmol">Element (Jmol)</Radio>
                                            <Radio value="carbon">Element (Carbon)</Radio>
                                        </Space>
                                    </Radio.Group>
                                    <Divider style={{ margin: "12px 0" }} />
                                    <Checkbox 
                                        checked={showLigandSurface} 
                                        onChange={(e) => setShowLigandSurface(e.target.checked)} 
                                        disabled={!showLigand}
                                    >
                                        Show Surface
                                    </Checkbox>
                                </div>
                            </Space>
                         </Panel>
                         <Panel header={<Text strong>Pharmacophore Controls</Text>} key="pharmacophore" style={{ border: "none" }}>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <Checkbox checked={showPharmacophore} onChange={(e) => setShowPharmacophore(e.target.checked)}>Show Pharmacophore</Checkbox>
                                <Divider style={{margin: "8px 0"}}/>
                                <div style={{opacity: showPharmacophore ? 1 : 0.5}}>
                                    <Checkbox checked={showFeatureLabels} onChange={(e) => setShowFeatureLabels(e.target.checked)} disabled={!showPharmacophore}>Show Feature Names</Checkbox>
                                    <div style={{marginTop: 8}}>
                                        <Text>Sphere Opacity</Text>
                                        <Slider min={0} max={1} step={0.05} value={glassSphereOpacity} onChange={setGlassSphereOpacity} disabled={!showPharmacophore}/>
                                    </div>
                                </div>
                            </Space>
                         </Panel>
                    </Collapse>
                </Sider>
                <Content>
                    <div ref={viewerDiv} style={{ width: "100%", height: "75vh", border: "1px solid #d9d9d9", borderRadius: "8px", background: "white", position: "relative" }}>
                        {(loading) && ( <div style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 10, backgroundColor: "rgba(255,255,255,0.7)"}}><Spin size="large" /><Text style={{marginTop: 16}}>Loading 3D Structures...</Text></div> )}
                        {error && ( <div style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, padding: 20}}><Alert message="Error" description={error} type="error" showIcon /></div> )}
                    </div>
                </Content>
            </Layout>
        </div>
    );
}
